import { useCallback, useEffect, useMemo } from "react";
import {
  useRecoilValue,
  useRecoilValueLoadable,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";

import { verifyExperimentOwnership } from "@func-core/core/application/services/experiment-service";
import { createResultAdapter } from "@func-core/core/data/adapters";
import { useExperimentStore, useResultStore } from "@func-core/core/data/repositories";
import {
  createResultViewModel,
  ResultDTO as ZustandResultDTO,
} from "@func-core/core/domain/view-models/result-view-model";
import { transformKeys } from "@utils/utils";
import { getUtmsFromQueryParams } from "@utils/utm";

import { Result, ResultDTO } from "@organisms/result/domain/result";
import {
  confirmResult,
  createResult,
  getPaymentUrl,
  prepareForBind,
  updateResult,
} from "@organisms/result/repositories/result-repository";
import {
  currentResultUuidState,
  dataLayerPropertiesState,
  identifyPropertiesState,
  resultState,
} from "@organisms/result/state/atoms";
import { useRecaptchaToken } from "@organisms/result/use-cases/use-recaptcha";

import { usePromotionCode } from "@features/quotes-funnel/application/promotion-code-use-cases";
import { useResetShoppingCart } from "@features/quotes-funnel/application/shopping-cart-use-cases";
import { preContractNotifiedState } from "@features/quotes-funnel/state/draft-contract-state";

export function useResultUuid(): string | null {
  const result = useRecoilValueLoadable(currentResultUuidState);
  switch (result.state) {
    case "hasValue":
      return result.contents;
    case "hasError":
      throw result.contents;
    default:
      return null;
  }
}

export function useResult(): Result | null {
  const result = useRecoilValueLoadable(resultState);
  const bridgeToZustand = useBridgeResultToZustand();

  useEffect(() => {
    if (result.state === "hasValue" && result.contents) {
      bridgeToZustand(result.contents);
    }
  }, [result.state, result.contents, bridgeToZustand]);

  switch (result.state) {
    case "hasValue": {
      if (!result.contents) {
        return null;
      }

      return new Result(result.contents);
    }
    case "hasError":
      throw result.contents;
    default:
      return null;
  }
}

export function useDataLayerProperties() {
  const data = useRecoilValueLoadable(dataLayerPropertiesState);
  switch (data.state) {
    case "hasValue":
      return data.contents;
    default:
      return null;
  }
}

export function useIdentifyProperties() {
  const data = useRecoilValueLoadable(identifyPropertiesState);
  switch (data.state) {
    case "hasValue":
      return data.contents;
    default:
      return null;
  }
}

export function usePrepareForBind() {
  const resultUuid = useResultUuid();
  if (!resultUuid) {
    return () => Promise.resolve();
  }

  return async () => {
    return prepareForBind(resultUuid);
  };
}

export function usePrepareResultCreationBody(): (
  values: Record<string, unknown>
) => Promise<string> {
  const getRecaptchaToken = useRecaptchaToken();

  return async (values: Record<string, unknown>): Promise<string> => {
    let requestBody = { ...values };

    const recaptchaToken = await getRecaptchaToken();
    if (recaptchaToken) {
      requestBody = { ...requestBody, recaptcha_token: recaptchaToken };
    }

    return JSON.stringify(requestBody);
  };
}

export function useCreateResult(): (
  values: Record<string, unknown>
) => Promise<ResultDTO | undefined> {
  const prepareBody = usePrepareResultCreationBody();
  const resetShoppingCart = useResetShoppingCart();

  return async (values: Record<string, unknown>): Promise<any> => {
    const requestBody = await prepareBody(values);

    resetShoppingCart();

    return createResult(requestBody);
  };
}

export function useSetResultState(): (values) => void {
  const setResult = useSetRecoilState(resultState);
  const result = useRecoilValueLoadable(resultState).contents;

  return (values) => {
    setResult({ ...result, ...values } as ResultDTO);
  };
}

export function useUpdateResult() {
  const resultUuid = useRecoilValue(currentResultUuidState);
  const setResult = useSetRecoilState(resultState);
  const preContractNotified = useRecoilValue(preContractNotifiedState);
  const resetPreContractNotified = useResetRecoilState(preContractNotifiedState);
  const bridgeToZustand = useBridgeResultToZustand();

  return useMemo(() => {
    if (!resultUuid) {
      return;
    }

    return async (values): Promise<Result> => {
      return updateResult(resultUuid, JSON.stringify(values)).then((updatedResult: ResultDTO) => {
        setResult(updatedResult);

        bridgeToZustand(updatedResult);
        if (preContractNotified) {
          resetPreContractNotified();
        }

        return new Result(updatedResult);
      });
    };
  }, [resultUuid, setResult, resetPreContractNotified, preContractNotified]);
}

export function useUpdateResultLastUtmParams() {
  const utmsFromQueryParams = useMemo(() => getUtmsFromQueryParams(), []);
  const resultUuid = useRecoilValue(currentResultUuidState);

  return async () => {
    if (!resultUuid || Object.keys(utmsFromQueryParams).length === 0) {
      return;
    }

    const prefixedLastUtms = transformKeys(utmsFromQueryParams, (key) => `last_${key}`);

    return updateResult(resultUuid, JSON.stringify(prefixedLastUtms));
  };
}

export function useUpdateCommercialAcceptedState(): (commercialCheckValue: boolean) => void {
  const setResult = useSetRecoilState(resultState);
  const result = useRecoilValueLoadable(resultState).contents;

  return (commercialCheckValue: boolean) => {
    setResult({ ...result, pet_parent_commercial_accepted: commercialCheckValue } as ResultDTO);
  };
}

export function useUpdatePaymentIntervalState(): (interval: number) => void {
  const setResult = useSetRecoilState(resultState);
  const result = useResult();

  return useCallback(
    (interval: number) => {
      if (interval !== result?.payment_interval_months) {
        setResult({ ...result, payment_interval_months: interval } as ResultDTO);
      }
    },
    [result, setResult]
  );
}

export function useConfirmResult(): () => Promise<unknown> {
  const resultUuid = useResultUuid();

  if (!resultUuid) {
    return () => Promise.resolve();
  }

  return async () => {
    return confirmResult(resultUuid);
  };
}

export function useGetPaymentUrl(): () => Promise<{ url: string }> {
  const resultUuid = useResultUuid();
  const promotionCode = usePromotionCode();

  if (!resultUuid) {
    return () => Promise.resolve({ url: "" });
  }

  const successUrl = `${process.env.GATSBY_URL_BASE}/results/${resultUuid}/checkout_success/`;
  const cancelUrl = `${process.env.GATSBY_URL_BASE}/results/${resultUuid}/details/`;
  const promotionCodeId = promotionCode?.id;

  return async () => {
    return getPaymentUrl(
      resultUuid,
      JSON.stringify({
        success_url: successUrl,
        cancel_url: cancelUrl,
        ...(promotionCodeId && { promotion_code_id: promotionCodeId }),
      })
    );
  };
}

export function useUpdateResultExperimentAssignments(): () => Promise<ResultDTO | undefined> {
  const { assignments = {} } = useExperimentStore.getState();
  const resultUuid = useRecoilValue(currentResultUuidState);
  const result = useResult();

  return async (): Promise<ResultDTO | undefined> => {
    if (!resultUuid || !result || Object.keys(assignments).length === 0) {
      return undefined;
    }

    const ownershipValid = verifyExperimentOwnership(result.pet_parent_kb_key, result.country);

    if (ownershipValid) {
      const experimentAssignments = Object.values(assignments).reduce<Record<string, string>>(
        (acc, assignment) => {
          acc[assignment.experimentId] = assignment.variantId;

          return acc;
        },
        {}
      );

      return updateResult(
        resultUuid,
        JSON.stringify({ experiment_assignments: experimentAssignments })
      );
    }

    return undefined;
  };
}

export const useBridgeResultToZustand = (): ((result: ResultDTO) => void) => {
  // Bridge between Zustand and Recoil
  // Remove this once the migration to Zustand is complete
  return useCallback((result: ResultDTO): void => {
    const dto = result as unknown as ZustandResultDTO;
    const adapter = createResultAdapter();
    const petParentData = adapter.getPetParentData(dto.uuid);
    const viewModel = createResultViewModel(dto, petParentData);
    useResultStore.getState().setResult(viewModel);
  }, []);
};
